/**
 * Iter-146 — Field-level Authoring Map.
 *
 * A toggleable overlay that, when enabled, dims every field on the
 * Contract Detail page that is NOT authored on that page, and shows a
 * label badge with the authoring location for each greyed-out region.
 *
 * Usage:
 *   1. Wrap any field that IS authored on this page with:
 *        <AuthoredHere fieldKey="title">{...input...}</AuthoredHere>
 *   2. Wrap any field that is authored ELSEWHERE with:
 *        <AuthoredElsewhere authoredIn="Sourcing Award" path="/app/sourcing/award-recommendation">
 *          {...read-only display...}
 *        </AuthoredElsewhere>
 *   3. Drop <AuthoringMapToggle/> in the page header.
 *
 * The state is shared via a tiny context so any descendant can opt in.
 * No layout changes when the overlay is off — every wrapper is a
 * transparent <span> in the default state.
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ArrowUpRight, MapPin, PencilLine, ExternalLink, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Ctx = createContext({ on: false, toggle: () => {} });

const LS_KEY = 'authoringMap.on';

export function AuthoringMapProvider({ children }) {
  // Persist toggle across page navigations so the user doesn't have to
  // re-enable it every time they click into a sub-module.
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; }
    catch { return false; }
  });
  const toggle = useCallback(() => setOn(v => {
    const next = !v;
    try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch {}
    return next;
  }), []);
  return <Ctx.Provider value={{ on, toggle }}>{children}</Ctx.Provider>;
}

export function useAuthoringMap() {
  return useContext(Ctx);
}

/* ─── Toggle button (drop into the page header) ─── */
export function AuthoringMapToggle() {
  const { on, toggle } = useAuthoringMap();
  return (
    <Button
      type="button"
      variant={on ? 'default' : 'outline'}
      size="sm"
      onClick={toggle}
      data-testid="authoring-map-toggle"
      className="gap-1.5 text-xs"
      title="Highlight fields authored on other pages"
    >
      <MapPin className="w-3.5 h-3.5"/>
      {on ? 'Authoring Map: ON' : 'Authoring Map'}
    </Button>
  );
}

/* ─── Wrapper for fields authored on THIS page ─── */
export function AuthoredHere({ fieldKey, children, className = '' }) {
  const { on } = useAuthoringMap();
  if (!on) return <span className={className}>{children}</span>;
  return (
    <span data-testid={`authored-here-${fieldKey}`}
          className={`relative inline-block ${className}
                       ring-2 ring-emerald-400 ring-offset-1 rounded-sm
                       transition-shadow`}>
      {children}
      <span className="absolute -top-2 -right-2 px-1 py-0.5 rounded
                         bg-emerald-500 text-white text-[8px] uppercase tracking-wider
                         shadow-sm pointer-events-none">
        Authored Here
      </span>
    </span>
  );
}

/* ─── Wrapper for fields authored ELSEWHERE ─── */
export function AuthoredElsewhere({
  authoredIn, path, fieldKey, children, className = '',
}) {
  const { on } = useAuthoringMap();
  const navigate = useNavigate();
  if (!on) return <span className={className}>{children}</span>;
  return (
    <span data-testid={`authored-elsewhere-${fieldKey || authoredIn}`}
          className={`relative inline-block ${className}
                       opacity-60 grayscale ring-1 ring-amber-300 rounded-sm
                       transition-all`}>
      {children}
      <button type="button"
              onClick={(e) => { e.stopPropagation(); if (path) navigate(path); }}
              className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded
                         bg-amber-500 text-white text-[9px] font-semibold uppercase
                         tracking-wider shadow-sm hover:bg-amber-600
                         flex items-center gap-0.5">
        {authoredIn} <ArrowUpRight className="w-2.5 h-2.5"/>
      </button>
    </span>
  );
}

/* ─── Read-only meta: which fields originate where (for the legend) ─── */
export function AuthoringMapLegend() {
  const { on } = useAuthoringMap();
  if (!on) return null;
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/60 p-2 mb-3
                     flex items-center gap-3 text-[11px]"
         data-testid="authoring-map-legend">
      <span className="font-semibold text-blue-900">Authoring Map active</span>
      <span className="flex items-center gap-1 text-emerald-700">
        <span className="w-2 h-2 rounded-full bg-emerald-500"/> Authored on this page
      </span>
      <span className="flex items-center gap-1 text-amber-700">
        <span className="w-2 h-2 rounded-full bg-amber-500"/> Authored elsewhere — click badge to jump
      </span>
      <span className="text-slate-500">
        Use this to learn where each piece of data originates from.
      </span>
    </div>
  );
}


/**
 * Iter-147 — Global Page-level Authoring Banner.
 *
 * Auto-renders on EVERY page when the Authoring Map toggle is ON, using
 * the `moduleKey` resolved by AppLayout + the `authoring` block already
 * defined in `config/moduleLinks.js`. This is the universal coverage
 * mechanism — pages don't have to opt in; if they have a moduleKey,
 * they get a banner explaining where their data comes from.
 *
 *  • If `location.pathname` matches `authoring.path` → "Authored here"
 *    green banner; the page is the source of truth for this data.
 *  • Otherwise → "Authored elsewhere" amber banner with a one-click
 *    deeplink to the authoring page. The page becomes visually dimmed
 *    so the user immediately understands this is a derived/read-only view.
 *
 * No per-page changes required.
 */
export function PageAuthoringBanner({ moduleKey, authoring }) {
  const { on } = useAuthoringMap();
  const location = useLocation();
  const navigate = useNavigate();
  if (!on || !authoring) return null;

  // "Authored here" if the current path matches the canonical authoring path
  // OR if the authoring.path is a prefix of the current path (e.g. detail screens).
  const authPath = (authoring.path || '').split('?')[0];
  const isAuthoredHere = !!authPath && (
    location.pathname === authPath ||
    location.pathname.startsWith(authPath + '/') ||
    // Treat /repository as authoring surface when wizard is /repository/new
    (authPath.startsWith(location.pathname + '/'))
  );

  if (isAuthoredHere) {
    return (
      <div className="rounded-md border-2 border-emerald-300 bg-emerald-50/70
                       px-3 py-2 mb-3 flex items-center gap-2 text-[11px]"
           data-testid={`page-authoring-banner-here-${moduleKey || 'generic'}`}>
        <PencilLine className="w-3.5 h-3.5 text-emerald-700 shrink-0"/>
        <span className="font-semibold text-emerald-900">Authored Here</span>
        <span className="text-emerald-800">— {authoring.where}.</span>
        {authoring.action && (
          <span className="text-emerald-700 italic">Action: {authoring.action}.</span>
        )}
        {authoring.note && (
          <span className="text-emerald-700/80 truncate">{authoring.note}</span>
        )}
      </div>
    );
  }

  // Otherwise authored elsewhere
  return (
    <div className="rounded-md border-2 border-amber-300 bg-amber-50/70
                     px-3 py-2 mb-3 flex items-center gap-2 text-[11px]"
         data-testid={`page-authoring-banner-elsewhere-${moduleKey || 'generic'}`}>
      <Eye className="w-3.5 h-3.5 text-amber-700 shrink-0"/>
      <span className="font-semibold text-amber-900">Read-Only View</span>
      <span className="text-amber-800">— data on this screen is authored at</span>
      <button
        type="button"
        onClick={() => authoring.path && navigate(authoring.path)}
        data-testid={`page-authoring-jump-${moduleKey || 'generic'}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded
                   bg-amber-600 text-white font-semibold hover:bg-amber-700
                   transition-colors"
      >
        {authoring.where} <ArrowUpRight className="w-3 h-3"/>
      </button>
      {authoring.note && (
        <span className="text-amber-700/80 truncate flex-1">{authoring.note}</span>
      )}
    </div>
  );
}


/**
 * Iter-147 — Page Frame
 *
 * When the Authoring Map is ON, wraps the page main content in a tinted
 * outline so users see at a glance whether they're on the authoring page
 * (green outline) or a downstream consumer (subtle amber outline).
 *
 * Safe no-op when toggle is OFF — just renders children.
 */
export function PageAuthoringFrame({ moduleKey, authoring, children }) {
  const { on } = useAuthoringMap();
  const location = useLocation();
  if (!on || !authoring) return <>{children}</>;
  const authPath = (authoring.path || '').split('?')[0];
  const isAuthoredHere = !!authPath && (
    location.pathname === authPath ||
    location.pathname.startsWith(authPath + '/'));
  return (
    <div className={`relative rounded-lg transition-all ${
      isAuthoredHere
        ? 'ring-2 ring-emerald-300 ring-offset-1'
        : 'ring-1 ring-amber-300/60 ring-offset-1'
    }`}
         data-testid={`page-authoring-frame-${isAuthoredHere ? 'here' : 'elsewhere'}`}>
      {children}
    </div>
  );
}

